import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { format } from 'date-fns'
import type { EmailSend } from '../types'

interface CommunicationHistoryProps {
  emails: EmailSend[]
  isLoading?: boolean
}

const statusVariants: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-gray-50 text-gray-700 border-gray-200' },
  sent: { label: 'Sent', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  delivered: { label: 'Delivered', className: 'bg-green-50 text-green-700 border-green-200' },
  opened: { label: 'Opened', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  clicked: { label: 'Clicked', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  bounced: { label: 'Bounced', className: 'bg-red-50 text-red-700 border-red-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
}

const emailTypeLabels: Record<string, string> = {
  transactional: 'Transactional',
  notification: 'Notification',
  engagement: 'Engagement',
  campaign: 'Campaign',
  admin_direct: 'Direct',
  admin_bulk: 'Bulk',
  artwork_published: 'Artwork Published',
  password_reset: 'Password Reset',
  account_verification: 'Verification',
}

export function CommunicationHistory({ emails, isLoading }: CommunicationHistoryProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Loading communication history...
      </div>
    )
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Activity</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {emails.map((email) => {
            const status = statusVariants[email.status] || statusVariants.pending
            return (
              <TableRow key={email.id}>
                <TableCell className="text-sm">
                  {email.sentAt
                    ? format(new Date(email.sentAt), 'MMM d, yyyy h:mm a')
                    : format(new Date(email.createdAt), 'MMM d, yyyy h:mm a')}
                </TableCell>
                <TableCell>
                  <p className="font-medium">{email.subject}</p>
                  {email.campaignName && (
                    <p className="text-xs text-muted-foreground">Campaign: {email.campaignName}</p>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {emailTypeLabels[email.emailType] || email.emailType}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={status.className}>
                    {status.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {email.openedAt && (
                    <p>Opened: {format(new Date(email.openedAt), 'MMM d, h:mm a')}</p>
                  )}
                  {email.clickedAt && (
                    <p>Clicked: {format(new Date(email.clickedAt), 'MMM d, h:mm a')}</p>
                  )}
                  {email.bouncedAt && (
                    <p className="text-red-600">Bounced: {email.bounceReason || 'Unknown'}</p>
                  )}
                  {email.errorMessage && (
                    <p className="text-red-600">Error: {email.errorMessage}</p>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
          {emails.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                No emails sent yet
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
